/**
 * 主题色 CSS 变量注入器。
 *
 * 职责：响应式监听全局主色，把派生的主题色 token 写到 document 根节点，
 * 使组件样式中引用的 var(--puppy-*) 始终跟随当前主题色整体变化。
 */
import { watch } from 'vue';
import { useGlobalConfigStore } from '@/stores';
import { createThemeColorTokens, themeColorTokensToCssVariables } from '@/utils';

/**
 * 启动主题色 token 注入。
 *
 * 必须在 Vue setup 上下文中调用；内部 watch 会随组件卸载自动停止。
 * App.vue 是根组件，生命周期与页面一致，因此变量始终存在。
 */
export function useThemeTokenInjector(): void {
  const globalStore = useGlobalConfigStore();

  const applyThemeTokens = (): void => {
    const tokens = createThemeColorTokens(globalStore.mainColor);
    const variables = themeColorTokensToCssVariables(tokens);
    const rootStyle = document.documentElement.style;

    for (const [variableName, value] of Object.entries(variables)) {
      rootStyle.setProperty(variableName, value);
    }
  };

  watch(
    () => globalStore.mainColor,
    applyThemeTokens,
    {
      immediate: true,
      // 主色对象可能被整体替换，也可能被就地修改，深监听保证两种路径都能触发。
      deep: true,
    },
  );
}
